// ── Enums / string unions ──────────────────────────────────────────────────────

export type ArWorkflowStatus =
  | 'Draft'
  | 'Submitted'
  | 'PendingApproval'
  | 'Approved'
  | 'Rejected'
  | 'Posted'
  | 'Cancelled'
  | 'Reversed';

export type ArPostingStatus = 'NotPosted' | 'Posted';

// ── Status display helpers ────────────────────────────────────────────────────

export const AR_WORKFLOW_STATUS_LABELS: Record<ArWorkflowStatus, string> = {
  Draft:          'Draft',
  Submitted:      'Submitted',
  PendingApproval:'Pending',
  Approved:       'Approved',
  Rejected:       'Rejected',
  Posted:         'Posted',
  Cancelled:      'Cancelled',
  Reversed:       'Reversed',
};

export const AR_WORKFLOW_STATUS_COLORS: Record<ArWorkflowStatus, string> = {
  Draft:          'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  Submitted:      'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  PendingApproval:'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
  Approved:       'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  Rejected:       'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  Posted:         'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  Cancelled:      'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
  Reversed:       'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
};

// ── Line DTO ──────────────────────────────────────────────────────────────────

export interface ArInvoiceLineDto {
  id:                   string;
  invoiceId:            string;
  lineNumber:           number;
  description?:         string | null;
  accountId:            string;
  accountCode?:         string | null;
  accountName?:         string | null;
  outputTaxAccountId?:  string | null;
  costCenterId?:        string | null;
  locationId?:          string | null;
  quantity:        number;
  unitPrice:       number;
  discountPercent: number;
  discountAmount:  number;
  lineAmount:      number;
  taxRate:         number;
  taxAmount:       number;
  totalAmount:     number;
  baseAmount:      number;
  currencyCode?:   string | null;
  exchangeRate:    number;
  remarks?:        string | null;
}

// ── Detail DTO ────────────────────────────────────────────────────────────────

export interface ArInvoiceDto {
  id:                   string;
  companyId:            string;
  locationId?:          string | null;
  costCenterId?:        string | null;
  financialYearCode:    string;
  financialPeriodCode:  string;
  customerId:           string;
  customerCode:         string;
  customerName:         string;
  controlAccountId?:    string | null;
  invoiceNumber:        string;
  invoiceDate:          string;
  postingDate:          string;
  dueDate?:             string | null;
  reference?:           string | null;
  narration?:           string | null;
  currencyCode:         string;
  exchangeRate:         number;
  workflowStatus:       ArWorkflowStatus;
  postingStatus:        ArPostingStatus;
  subTotal:             number;
  taxAmount:            number;
  totalAmount:          number;
  baseAmount:           number;
  submittedBy?:         string | null;
  submittedDate?:       string | null;
  approvedBy?:          string | null;
  approvedDate?:        string | null;
  rejectedBy?:          string | null;
  rejectedDate?:        string | null;
  rejectionReason?:     string | null;
  postedBy?:            string | null;
  postedDate?:          string | null;
  cancelledBy?:         string | null;
  cancelledDate?:       string | null;
  cancellationReason?:  string | null;
  isReversal:           boolean;
  originalInvoiceId?:   string | null;
  reversalInvoiceId?:   string | null;
  sourceGlJournalId?:   string | null;
  createdBy?:           string | null;
  createdDate:          string;
  modifiedBy?:          string | null;
  modifiedDate?:        string | null;
  lines:                ArInvoiceLineDto[];
}

// ── List row DTO ──────────────────────────────────────────────────────────────

export interface ArInvoiceListDto {
  id:              string;
  invoiceNumber:   string;
  invoiceDate:     string;
  postingDate:     string;
  dueDate?:        string | null;
  customerCode:    string;
  customerName:    string;
  currencyCode:    string;
  totalAmount:     number;
  baseAmount:      number;
  workflowStatus:  ArWorkflowStatus;
  postingStatus:   ArPostingStatus;
  reference?:      string | null;
  createdDate:     string;
}

// ── Filter DTO ────────────────────────────────────────────────────────────────

export interface ArInvoiceFilterDto {
  companyId?:          string;
  locationId?:         string;
  costCenterId?:       string;
  customerId?:         string;
  customerCode?:       string;
  invoiceNumber?:      string;
  workflowStatus?:     string;
  postingStatus?:      string;
  financialYearCode?:  string;
  financialPeriodCode?: string;
  dateFrom?:           string;
  dateTo?:             string;
  reference?:          string;
  search?:             string;
  pageNumber?:         number;
  pageSize?:           number;
  sortBy?:             string;
  sortDescending?:     boolean;
}

// ── Line request ──────────────────────────────────────────────────────────────

export interface ArInvoiceLineRequest {
  lineNumber:          number;
  description?:        string | null;
  accountId:           string;
  outputTaxAccountId?: string | null;
  costCenterId?:       string | null;
  locationId?:         string | null;
  quantity:        number;
  unitPrice:       number;
  discountPercent: number;
  taxRate:         number;
  remarks?:        string | null;
}

// ── Create / Update requests ──────────────────────────────────────────────────

export interface CreateArInvoiceRequest {
  locationId?:      string | null;
  locationCode?:    string | null;
  costCenterId?:    string | null;
  customerId:       string;
  customerCode:     string;
  customerName:     string;
  controlAccountId?: string | null;
  invoiceDate:      string;
  postingDate:      string;
  dueDate?:         string | null;
  currencyCode:     string;
  exchangeRate:     number;
  reference?:       string | null;
  narration?:       string | null;
  lines:            ArInvoiceLineRequest[];
}

export interface UpdateArInvoiceRequest {
  locationId?:      string | null;
  costCenterId?:    string | null;
  customerId:       string;
  customerCode:     string;
  customerName:     string;
  controlAccountId?: string | null;
  invoiceDate:      string;
  postingDate:      string;
  dueDate?:         string | null;
  currencyCode:     string;
  exchangeRate:     number;
  reference?:       string | null;
  narration?:       string | null;
  lines:            ArInvoiceLineRequest[];
}
